import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Paper, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { getStudent } from 'state/modules/entity/selectors/student-selectors';
import { AppStoreState, ASSIGNMENT_VIEW_TYPE } from 'types';
import { SATableHeader } from './SATableHeader';
import { Messenger } from './Messenger/Messenger';
import { AssignmentToggleNav } from './AssignmentToggleNav';
import { AssignmentAttemptsContent } from './AssignmentAttemptsContent';
import AssignmentDetails from './AssignmentDetails';
import AttemptStatusChip from './AttemptStatusChip';
import { translate } from 'translations/translator';
import { getAssignmentAttempt } from 'state/modules/entity/custom/assignment-attempt-mutations/thunks';
import { fetchUserClassrooms } from 'state/modules/entity/custom/classrooms/thunks';
import { formatUiDate } from 'util/date-util';

const getTranslation = (key, options?) => translate('assignmentTracker', key, options);

export const AssignmentAttempt = () => {
  const navigate = useNavigate();
  const { assignmentId: assignmentIdParam, studentId: studentIdParam } = useParams();
  const assignmentId = Number(assignmentIdParam);
  const studentId = Number(studentIdParam);
  const theme = useTheme();
  const classes = useStyles(theme)();
  const dispatch = useDispatch() as any;
  const [selected, setSelected] = React.useState(ASSIGNMENT_VIEW_TYPE.ASSIGNMENTS);
  const student = useSelector(state => getStudent(state, { studentId })) ?? null;
  const messages = useSelector(({ entity }: AppStoreState) => entity.assignmentAttemptMessages);
  const assignmentAttempt = useSelector(({ entity }: AppStoreState) => entity.assignmentAttempt);
  const students = useSelector(({ entity }: AppStoreState) => entity.students) ?? [];
  const studentName = `${student?.firstName} ${student?.lastName}`;
  const handlePreviousClick = () => navigate(-1);

  useEffect(() => {
    dispatch(getAssignmentAttempt(assignmentId, studentId));
    if (students.length === 0) {
      dispatch(fetchUserClassrooms());
    }
  }, [dispatch, assignmentId, studentId, students]);

  if (!student || !assignmentAttempt) return null;

  return (
    <Box px={5} pt={3} pb={5} bgcolor="primary.light" className={classes.container}>
      <SATableHeader
        title={studentName}
        renderBackArrow={true}
        handlePreviousClick={() => handlePreviousClick()}
      />
      <AssignmentDetails
        style={{ marginBottom: 20 }}
        fields={[
          {
            label: getTranslation('assignmentStatus'),
            value: (
              <Box ml="auto">
                <AttemptStatusChip status={assignmentAttempt.status} />
              </Box>
            )
          },
          {
            label: getTranslation('dateAssigned'),
            value: formatUiDate(new Date(assignmentAttempt.assignment.dateAssigned))
          },
          {
            label: getTranslation('dateDue'),
            value: formatUiDate(new Date(assignmentAttempt.assignment.dateDue))
          }
        ]}
      />
      <Paper elevation={3}>
        <AssignmentToggleNav handleSelected={setSelected} selected={selected} />
        {selected === ASSIGNMENT_VIEW_TYPE.MESSAGES ? (
          <Messenger assignmentId={assignmentId} studentId={studentId} messages={messages} />
        ) : (
          <AssignmentAttemptsContent />
        )}
      </Paper>
    </Box>
  );
};

const footerAndHeaderHeight = 170;
const useStyles = theme =>
  makeStyles(() => ({
    container: {
      height: `calc(100vh - ${footerAndHeaderHeight}px)`,
      overflow: 'auto'
    },
    titleHeader: () => ({
      paddingBottom: theme.spacing(2)
    })
  }));
